import { useState, type FC } from 'react'

import { MessagePopup, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import CheckCircleIcon from 'mdi-react/CheckCircleIcon'
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon'
import SendIcon from 'mdi-react/SendIcon'

import { useTranslation } from '@/app/translations/translations-hooks'
import adminQueries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { UpdateOrdersType } from '@/modules/admin/resources/trade-in'
import type { Status } from '@/modules/admin/resources/trade-in/getTradeInOrders'

export const Actions: FC<{
  orderStatus: Status
  orderId: string
  customerId: number
}> = ({ orderStatus, orderId, customerId }) => {
  const translate = useTranslation()

  if (!customerId) throw Error(translate('Customer ID missing!'))
  if (!orderId) throw Error(translate('Order guid missing!'))

  const queryClient = useQueryClient()
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)
  const rejectTooltipText =
    orderStatus === 'ACCEPTANCE' ? translate('Last chance to cancel') : translate('Reject')

  const updateTradeInOrdersMutation = useMutation(adminResources.updateTradeInOrders, {
    onError() {
      setErrorPopupMessage({
        text: translate("We couldn't update status for this order. Please try again later."),
        title: translate('Update order status error.'),
      })
    },
    onSettled() {
      queryClient.invalidateQueries(adminQueries.adminTradeIdOrdersList(customerId), {
        refetchType: 'all',
      })
    },
  })

  const handleStatusUpdate = (status: Status) => {
    const payload: UpdateOrdersType = {
      orders: [{ comment: '', orderId: orderId, newStatusCode: status }],
    }
    updateTradeInOrdersMutation.mutate({ payload })
  }

  return (
    <div className="flex gap-1">
      {orderStatus === 'PRICE_ESTIMATION' && (
        <AcceptAction
          tooltipText={translate('Accept')}
          onClick={() => handleStatusUpdate('ACCEPTANCE')}
        />
      )}

      {orderStatus === 'ACCEPTANCE' && (
        <SendAction tooltipText={translate('Send')} onClick={() => handleStatusUpdate('SENT')} />
      )}

      <Tooltip id="reject-button-tooltip" style={{ zIndex: 20 }} />
      <span data-tooltip-id="reject-button-tooltip" data-tooltip-content={rejectTooltipText}>
        <Button
          className="m-0 p-0 text-red-400 hover:enabled:text-red-700 focus:enabled:text-red-700 disabled:text-gray-300"
          variant="tertiary"
          Icon={<CloseCircleOutlineIcon />}
          onClick={e => {
            e.stopPropagation()
            handleStatusUpdate('REJECTED')
          }}
        />
      </span>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
      />
    </div>
  )
}

const AcceptAction: FC<{ tooltipText: string; onClick: () => void }> = ({
  tooltipText,
  onClick,
}) => (
  <>
    <Tooltip id="accept-button-tooltip" style={{ zIndex: 20 }} />
    <span data-tooltip-id="accept-button-tooltip" data-tooltip-content={tooltipText}>
      <Button
        className="m-0 p-0 text-green-500 hover:enabled:text-green-700 focus:enabled:text-green-700 disabled:text-gray-300"
        variant="tertiary"
        Icon={<CheckCircleIcon />}
        onClick={e => {
          e.stopPropagation()
          onClick()
        }}
      />
    </span>
  </>
)

const SendAction: FC<{ tooltipText: string; onClick: () => void }> = ({ tooltipText, onClick }) => (
  <>
    <Tooltip id="send-button-tooltip" style={{ zIndex: 20 }} />
    <span data-tooltip-id="send-button-tooltip" data-tooltip-content={tooltipText}>
      <Button
        className="text-primary m-0 p-0 disabled:text-gray-300"
        variant="tertiary"
        Icon={<SendIcon />}
        onClick={e => {
          e.stopPropagation()
          onClick()
        }}
      />
    </span>
  </>
)
